import { ApiMethod } from "@/api/apiMethod";
import { isExcelReadOnlyField } from "@/excel/excelReadOnlyField";
import type {
  ExcelFunction,
  ExcelFunctionParameter,
  FailedEndpoint,
} from "@/services/custom-functions/customFunction.types";
import type {
  ExtendedDocument,
  ExtendedOperationObject,
  ExtendedSchemaObject,
  JsonSchemaType,
} from "@/services/open-api/openApi.types";
import { constructUrl } from "@/utils/utils";

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

const EXCEL_DIMENSIONALITY = {
  scalar: "scalar",
  matrix: "matrix",
} as const;

const TYPE_MAPPING = {
  string: "string",
  integer: "number",
  boolean: "boolean",
} as const;

type TypeResult = {
  type: string;
  nestLevel: number;
};

function toSingleType(type: JsonSchemaType | undefined) {
  return Array.isArray(type) ? type.find((item) => item !== "null") : type;
}

function getType(details: ExtendedSchemaObject, nestLevel: number = 0): TypeResult {
  let type: string | null = null;
  const detailsType = toSingleType(details.type);

  if ("anyOf" in details) {
    // AnyOf is to support Unions or Any types
    type = "string";
  } else if (!detailsType && details?.enum) {
    // Handle enum types without explicit type - treat as string
    type = "string";
  } else if (!detailsType) {
    throw new Error("Complex types are not supported, only primitive types are supported");
  } else {
    type = TYPE_MAPPING[detailsType as keyof typeof TYPE_MAPPING]
      ? TYPE_MAPPING[detailsType as keyof typeof TYPE_MAPPING]
      : detailsType;
  }

  if (type === "array") {
    nestLevel++;

    const itemSchema = details.items;

    if (!itemSchema) {
      // Sometimes there are no items but e.g. prefixItems, these should be put in the right type by the api
      // Later it can be investigated how this can be done correctly in excel, however it seems like a matrix of values needs to have the same type
      type = "string";
    } else {
      return getType(itemSchema, nestLevel);
    }
  }

  if (nestLevel > 2) {
    throw Error("Required nested level of list is not support by Excel Custom Functions");
  }

  return { type, nestLevel };
}

class GetMethodConstructor {
  private details: any;
  private schema: ExtendedDocument;

  constructor(details: ExtendedOperationObject, schema: ExtendedDocument) {
    this.details = details;
    this.schema = schema;
  }

  construct(): ExcelFunctionParameter[] {
    const parameters: ExcelFunctionParameter[] = [];

    if (this.details.parameters) {
      this.details.parameters.forEach((param: any) => {
        const typeResult = getType({ type: param.schema.type });

        parameters.push({
          description: param.description,
          name: param.name,
          type: typeResult.type,
          nestLevel: typeResult.nestLevel,
        });
      });
    }

    return parameters;
  }
}

class PostMethodConstructor {
  private details: any;
  private schema: ExtendedDocument;

  constructor(details: ExtendedOperationObject, schema: ExtendedDocument) {
    this.details = details;
    this.schema = schema;
  }

  construct(): ExcelFunctionParameter[] {
    if (!this.details.requestBody?.content["application/json"]?.schema.$ref) {
      return [];
    }

    const ref = this.details.requestBody.content["application/json"].schema.$ref;
    const requestBodySchema = this.getRequestBodySchema(ref);

    if (!requestBodySchema.properties) {
      return [];
    }

    return this.mapPropertiesToParameters(requestBodySchema);
  }

  private getRequestBodySchema(ref: string): any {
    const schemaName = ref.split("/").pop();

    if (!schemaName) {
      throw new Error(`Invalid request body schema reference "${ref}".`);
    }

    const schema = this.schema.components?.schemas?.[schemaName];

    if (!schema) {
      throw new Error(`Referenced schema "${schemaName}" was not found in OpenAPI components.schemas.`);
    }

    return schema;
  }

  private mapPropertiesToParameters(schema: any): ExcelFunctionParameter[] {
    const parameters: ExcelFunctionParameter[] = [];
    const requiredNames = new Set<string>(Array.isArray(schema.required) ? schema.required : []);

    for (const [name, details] of Object.entries<any>(schema.properties ?? {})) {
      const isRequiredWithoutDefault =
        requiredNames.has(name) && !Object.prototype.hasOwnProperty.call(details, "default");

      if (!isRequiredWithoutDefault && isExcelReadOnlyField(name, details)) {
        continue;
      }

      if (details.$ref) {
        this.handleRefProperty(name, details, parameters);
      } else if (details.type === "array" && details.items && details.items.$ref) {
        this.handleArrayWithRefItems(name, details, parameters);
      } else {
        this.handlePrimitiveProperty(name, details, parameters);
      }
    }

    return parameters;
  }

  private handleRefProperty(name: string, details: any, parameters: ExcelFunctionParameter[]): void {
    const refSchema = this.getRequestBodySchema(details.$ref);
    if (!refSchema) return;

    if (refSchema.properties) {
      const nestedParams = this.mapPropertiesToParameters(refSchema);
      nestedParams.forEach((param) => {
        parameters.push({
          ...param,
          name: `${name}.${param.name}`,
          description: param.description,
        });
      });
    } else {
      const dimensionality = refSchema.type === "array" ? EXCEL_DIMENSIONALITY.matrix : EXCEL_DIMENSIONALITY.scalar;
      const { type, nestLevel } = getType(refSchema);
      parameters.push({
        description: refSchema.title || details.title || name,
        name,
        type,
        dimensionality,
        nestLevel,
      });
    }
  }

  private handleArrayWithRefItems(name: string, details: any, parameters: ExcelFunctionParameter[]): void {
    const itemRefSchema = this.getRequestBodySchema(details.items.$ref);
    if (!itemRefSchema) return;

    if (itemRefSchema.properties) {
      const nestedParams = this.mapPropertiesToParameters(itemRefSchema);
      nestedParams.forEach((param) => {
        parameters.push({
          ...param,
          name: `${name}[].${param.name}`,
          description: param.description,
          dimensionality: EXCEL_DIMENSIONALITY.matrix,
        });
      });
    } else {
      const { type } = getType(itemRefSchema);
      parameters.push({
        description: itemRefSchema.title || details.title || name,
        name,
        type,
        dimensionality: EXCEL_DIMENSIONALITY.matrix,
        nestLevel: 1,
      });
    }
  }

  private handlePrimitiveProperty(name: string, details: any, parameters: ExcelFunctionParameter[]): void {
    const dimensionality = details.type === "array" ? EXCEL_DIMENSIONALITY.matrix : EXCEL_DIMENSIONALITY.scalar;
    const { type, nestLevel } = getType(details);

    parameters.push({
      description: details.title || name,
      name,
      type,
      dimensionality,
      nestLevel,
    });
  }
}

export class OpenApiExcelFunctionConstructor {
  private schema: ExtendedDocument;
  private baseUrl: string;
  public notSupportedApiEndpointDetails: FailedEndpoint[] = [];

  public get notSupportedApiEndpoints(): string[] {
    return this.notSupportedApiEndpointDetails.map((endpoint) => endpoint.endpointId);
  }

  static ApiMethodConstructorStrategy = {
    [ApiMethod.GET]: GetMethodConstructor,
    [ApiMethod.POST]: PostMethodConstructor,
  };

  constructor(schema: ExtendedDocument, baseUrl: string) {
    this.schema = schema;
    this.baseUrl = baseUrl;
  }

  construct(): ExcelFunction[] {
    const functions: ExcelFunction[] = [];

    for (const [path, methods] of Object.entries(this.schema.paths ?? {})) {
      for (const [method, details] of Object.entries(methods ?? {})) {
        const typedMethod = method as ApiMethod;
        const functionName = this.getFunctionName(path);

        try {
          const parameters = this.getParameters(typedMethod, details as ExtendedOperationObject);

          const objectParameters = parameters.filter((param) => param.type === "object");
          if (objectParameters.length > 0) {
            throw new Error(
              `Object parameters are not supported by Excel custom functions: ${objectParameters
                .map((param) => param.name)
                .join(", ")}`
            );
          }

          if (parameters.length >= 255) {
            throw new Error(
              `Too many parameters for Excel custom function "${functionName}". Generated ${parameters.length} parameters.`
            );
          }

          functions.push({
            id: functionName,
            name: `${functionName}`,
            description: functionName,
            parameters,
            result: {
              type: "string",
            },
            url: constructUrl(this.baseUrl, path),
            method: typedMethod,
            options: {
              requiresAddress: true,
              requiresParameterAddresses: true,
            },
          });
        } catch (error: unknown) {
          const upperCaseMethod = String(method).toUpperCase();
          const reason = getErrorMessage(
            error,
            `Endpoint ${upperCaseMethod} ${path} is not supported by Excel function construction.`
          );

          this.notSupportedApiEndpointDetails.push({
            endpointId: functionName,
            method: upperCaseMethod,
            path,
            reason,
          });

          console.warn(`Error constructing Excel function for ${upperCaseMethod} ${path}:`, error);
        }
      }
    }

    return functions;
  }

  private getFunctionName(path: string): string {
    const name = path.replace(/^\/|\/$/g, "").replace(/[/-]+/g, "_");

    return /^[A-Za-z]/.test(name) ? name : `func${name}`;
  }

  private getParameters(method: ApiMethod, details: ExtendedOperationObject): ExcelFunctionParameter[] {
    const apiMethodConstructorStrategy = this.getApiMethodConstructorStrategy(method);
    const constructor = new apiMethodConstructorStrategy(details, this.schema);
    return constructor.construct();
  }

  private getApiMethodConstructorStrategy(
    method: ApiMethod
  ): typeof GetMethodConstructor | typeof PostMethodConstructor {
    const methodConstructorStrategy =
      OpenApiExcelFunctionConstructor.ApiMethodConstructorStrategy[
        method as keyof typeof OpenApiExcelFunctionConstructor.ApiMethodConstructorStrategy
      ];

    if (!methodConstructorStrategy) {
      throw new Error(`Method ${method} is not supported`);
    }

    return methodConstructorStrategy;
  }
}
